import { z } from "zod";

const VALIDATION_RULE_OPERATORS = [
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
  "true",
  "false",
  "regex",
  "contains",
  "not_contains",
  "starts_with",
  "ends_with",
] as const;

export const NoteSchema = z.union([z.string(), z.array(z.string())]);

export const EnumRuleSchema = z.union([
  z.array(z.string()),
  z
    .object({
      values: z.array(z.string()),
      message: z.string().optional(),
    })
    .strict(),
]);

export const FilterBySchema = z.union([
  z.boolean(),
  z.array(z.string()),
  z
    .object({
      operators: z.array(z.string()),
      scalarType: z.string().optional(),
    })
    .strict(),
]);

export const SearchableSchema = z.union([
  z.boolean(),
  z
    .object({
      enabled: z.boolean().optional(),
      phonetic: z.boolean().optional(),
    })
    .strict(),
]);

export const ValidationRuleSchema = z
  .object({
    operator: z.enum([...VALIDATION_RULE_OPERATORS] as [
      (typeof VALIDATION_RULE_OPERATORS)[number],
      ...(typeof VALIDATION_RULE_OPERATORS)[number][],
    ]),
    stringValue: z.string().optional(),
    numberValue: z.number().optional(),
    booleanValue: z.boolean().optional(),
    message: z.string().optional(),
  })
  .strict();

export const StateMachineStateSchema = z.union([
  z.string(),
  z
    .object({
      name: z.string().min(1),
      isFinal: z.boolean().optional(),
    })
    .strict(),
]);

export const StateMachineTransitionSchema = z
  .object({
    name: z.string().min(1),
    from: z.string().min(1),
    to: z.string().min(1),
  })
  .strict();

export const StateMachineSchema = z
  .object({
    name: z.string().min(1),
    entryState: z.string().min(1),
    states: z.array(StateMachineStateSchema).min(1),
    transitions: z.array(StateMachineTransitionSchema),
    finalStates: z.array(z.string()).optional(),
  })
  .strict();

const POLICY_OPERATORS = [
  "eq",
  "neq",
  "gt",
  "gte",
  "lt",
  "lte",
  "contains",
  "not_contains",
  "starts_with",
  "ends_with",
  "exists",
] as const;

const PolicyPredicateSchema = z
  .object({
    path: z.union([z.string(), z.array(z.string())]).optional(),
    field: z.string().optional(),
    input: z.string().optional(),
    operator: z.enum([...POLICY_OPERATORS] as [
      (typeof POLICY_OPERATORS)[number],
      ...(typeof POLICY_OPERATORS)[number][],
    ]),
    stringValue: z.string().optional(),
    numberValue: z.number().optional(),
    booleanValue: z.boolean().optional(),
    value: z.union([z.string(), z.number(), z.boolean(), z.null()]).optional(),
  })
  .strict();

const PolicyStateMachinePredicateSchema = z
  .object({
    machine: z.string().min(1),
    operator: z.enum([...POLICY_OPERATORS] as [
      (typeof POLICY_OPERATORS)[number],
      ...(typeof POLICY_OPERATORS)[number][],
    ]),
    state: z.string().optional(),
    stringValue: z.string().optional(),
  })
  .strict();

const PolicyConditionSchema: z.ZodTypeAny = z.lazy(() =>
  z
    .object({
      all: z.array(PolicyConditionSchema).optional(),
      any: z.array(PolicyConditionSchema).optional(),
      not: PolicyConditionSchema.optional(),
      input: PolicyPredicateSchema.optional(),
      object: PolicyPredicateSchema.optional(),
      stateMachine: PolicyStateMachinePredicateSchema.optional(),
    })
    .strict()
    .refine(
      (data) =>
        [
          data.all,
          data.any,
          data.not,
          data.input,
          data.object,
          data.stateMachine,
        ].filter((value) => value !== undefined).length === 1,
      {
        message:
          "Policy condition must define exactly one of all, any, not, input, object, or stateMachine",
      },
    ),
);

const PolicyRuleSchema = z
  .object({
    id: z.string().min(1).optional(),
    reason: z.string().optional(),
    when: PolicyConditionSchema,
  })
  .strict();

const PoliciesSchema = z
  .object({
    allowWhen: z.array(PolicyRuleSchema).optional(),
    confirmWhen: z.array(PolicyRuleSchema).optional(),
    denyWhen: z.array(PolicyRuleSchema).optional(),
  })
  .strict();

export const EffectMetamodelSchema = z
  .object({
    postCondition: z
      .union([
        z.string(),
        z
          .object({
            condition: z.string().min(1),
            description: z.string().optional(),
          })
          .strict(),
      ])
      .optional(),
    dryRun: z
      .union([
        z.boolean(),
        z
          .object({
            enabled: z.boolean().optional(),
            description: z.string().optional(),
          })
          .strict(),
      ])
      .optional(),
    reverse: z
      .union([
        z.string(),
        z
          .object({
            handler: z.string().optional(),
            description: z.string().optional(),
          })
          .strict(),
      ])
      .optional(),
    approvalRequired: z
      .union([
        z.boolean(),
        z
          .object({
            required: z.boolean().optional(),
            reason: z.string().optional(),
            mode: z.string().optional(),
          })
          .strict(),
      ])
      .optional(),
    access: z.enum(["read", "write", "ui"]).optional(),
    effectKind: z.enum(["read", "write", "ui"]).optional(),
    sideEffect: z
      .enum(["read", "write", "ui", "readonly", "read_only"])
      .optional(),
    policies: PoliciesSchema.optional(),
  })
  .strict();
